import * as React from 'react';

import { 
    Button, 
    Box, 
    Typography, 
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';

import { Confirmation, InputDialog } from '../utils';
import { TokenValidator, CONST, LoggerUtils, SimpleValidators } from '../../utils';
import { Parts, Logger, Candid, Orders} from '../../controllers';
import TrackingWorkspace from '../trackStations/internal/trackingWorkspace';
import useLegacyAction,{requireLegacySuccess} from '../trackStations/internal/legacyAction';

export default function Main(){
    return <TrackingWorkspace screen='QA_FINAL' renderLegacy={({order,onBusy})=><LegacyOrderReader key={order?.orderNumber||'legacy'} initialOrder={order} onBusy={onBusy}/>}/>;
}
export function LegacyOrderReader({initialOrder,onBusy}) {
    const runLegacy=useLegacyAction(onBusy,()=>({order:selected}));

    const [order, setOrder] = React.useState('');
    const [selected, setSelected] = React.useState('');
    const [parts, setParts] = React.useState([]);
    const [errors, setErrors] = React.useState([]);
    const [errorLog, setErrorLog] = React.useState('');

    const [co, isCo] = React.useState(false);
    const [coType, setCoType]  = React.useState(0);
    const [coNumber, setCoNumber] = React.useState('');
    const [coData, setCoData] = React.useState({
        dhr: 0,
        counter : 0
    })

    //UI Confirmations
    const [pass, showPass] = React.useState(false);
    const [decline, showDecline] = React.useState(false);

    //CO-UI Confirmations
    const [coProxes, showCoProxes] = React.useState(false);

    const handleFetch = runLegacy(async(e,scannedOrder=order) => {
        e.preventDefault();
        isCo(false);
        setParts([]);setSelected('');

        const status = "QAS Init";
        const result = await Parts.GetTracking("", scannedOrder, status);

        if(!result.success){
            if(result.message){
                alert(result.message);
            }else{
                alert('Error while fetching order information')
            }
            return;
        }

        if(result.result.length < 1){

            const response = await Candid.GetTracingReportByOrder(scannedOrder);

            if(response === undefined){
                alert('Error al leer Orden');
                return;
            }

            if(response.success && response.found){

                isCo(true);
                setParts([]);
                setSelected(scannedOrder);
                setCoNumber(response.result.case_serial);
                return;
            }

            alert('Orden escaneada no valida');
        }

        setParts(result.result);
        setSelected(scannedOrder);
        setOrder('');
    });

    const initialLoaded=React.useRef(false),initialFetch=React.useRef(handleFetch);
    initialFetch.current=handleFetch;
    React.useEffect(()=>{
        if(initialOrder&&!initialLoaded.current){initialLoaded.current=true;initialFetch.current({preventDefault:()=>{}},initialOrder.orderNumber);}
    },[initialOrder]);

    const handlePass = runLegacy(async() => {
        const targetStatus = 'QAS Complete';

        const token = '';
        
        for(const part of parts){
            const logType = 'Update'
            const logTitle = `Tracking Station QAS : ${logType}`
            const logInfo = 'Part processed through area successfully'

            const status = targetStatus;
            const payload = {
                id          : part.id,
                status      : status,
            }

            const log = await LoggerUtils.PayloadCreator( logType, logTitle, logInfo);
            const response = await Parts.UpdateStatus(token, {...payload,audit:log});
            requireLegacySuccess(response);
        }

        const log = await LoggerUtils.PayloadCreator('Update', 'Complete', 'Order completed');
        const response = await Orders.SetComplete({order:Number(selected),audit:log});
        requireLegacySuccess(response);

        alert('Order set as completed');
        showPass(false);
        setParts([]);
    },{write:true});

    const handleDecline = runLegacy(async() => {
        const targetStatus = 'QAS Error';

        const token = '';
        
        for(const part of parts){
            const logType = 'Error';
            const logTitle = `Tracking Station QAS : ${logType}`
            const logInfo = 'Part rejected by QA'

            const status = targetStatus;
            const payload = {
                id          : part.id,
                status      : status,
            }

            const log = await LoggerUtils.PayloadCreator( logType, logTitle, logInfo);
            const response = await Parts.UpdateStatus(token, {...payload,audit:log});
            requireLegacySuccess(response);
        }

        showDecline(false);
        setParts([]);
    },{write:true});

    const handleCoProcessing = runLegacy(async() =>{
        showCoProxes(false);

        const payload = {
            order : String(selected).toUpperCase(),
            data : {
                order : coNumber,
                area : {
                    name : 'QAS',
                    id : 10,
                },
                number : selected
            }
        }

        const response = await Candid.CreateTracing(payload);
        requireLegacySuccess(response);

        if(response == undefined){
            alert('Error Processing Order. \nPlease, try again');
            return;
        }

        if(!response.success){
            alert('Processing Order fail. \nPlease, try again');
            return;
        }

        isCo(false);
        setOrder('');
        setCoType(0);
        alert('Order Processed successfully');
    },{write:true});

    return(
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: 2
            }}
        >

            <Confirmation 
                open={pass}
                onClose={() => showPass(false)}
                onConfirm={handlePass}
                title={'Pass Confirmation'}
                msg={'The order will be set as completed'}
                info={false}
            />
            
            <Confirmation 
                open={decline}
                onClose={() => showDecline(false)}
                onConfirm={handleDecline}
                title={'No-Pass Confirmation'}
                msg={'The order will be set on hold for inspection'}
                info={false}
            />

            <Confirmation 
                open={coProxes}
                onClose={() => showCoProxes(false)}
                onConfirm={handleCoProcessing}
                title={'Confirmation'}
                msg={'The order will be set as completed'}
                info={false}
            />

            <br/>

            <Typography variant='h5'>
                Scanner Module
            </Typography>
            <Box 
                sx={{ mt: 1 }} 
                component='form'
                onSubmit={handleFetch}
            >
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    label="ESCANEA :  Order Id"
                    id="order"
                    name="order"
                    autoFocus
                    value={order}
                    onChange={e => setOrder(e.target.value)}
                />
            </Box>

            <Divider 
                sx={{
                    marginTop:1,
                    minWidth:'95vw',
                    borderColor:CONST.Colors.Primary.Main
                }}
            />

            {parts.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Typography variant='h6'>
                        Order ID:{selected}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            gap: 4, 
                            paddingBottom: 2,
                        }}
                    >
                        
                        <Button
                            color='success'
                            variant='contained'
                            fullWidth
                            onClick={handlePass}
                        >
                            PASS
                        </Button>

                        <Button
                            color='error'
                            variant='contained'
                            fullWidth
                            onClick={handleDecline}
                        >
                            NO PASS
                        </Button>

                    </Box>
                        
                    <Divider 
                        sx={{
                            minWidth:'95vw',
                            borderColor:CONST.Colors.Primary.Main
                        }}
                    />
                </Box>
            )}

            {parts.length <= 0 && co && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            padding: 2,
                        }}
                    >
                        <Typography variant='h6' sx={{width:'30%'}}>
                            Candid Order || Case ID: {coNumber}
                        </Typography>
                        
                        <Button
                            color='success'
                            variant='contained'
                            onClick={() => showCoProxes(true)}
                            fullWidth
                        >
                            PASS
                        </Button>
                        
                    </Box>
                        
                    <Divider 
                        sx={{
                            minWidth:'95vw',
                            borderColor:CONST.Colors.Primary.Main
                        }}
                    />

                </Box>
            )}

        </Box>
    );
}
